import { ButtonPrimary, IconButton } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { Status } from "@nova/ds-component-status"
import { Table } from "@nova/ds-component-table"
import { Tag } from "@nova/ds-component-tag"
import { MissionTableItem, MissionOriginRouterState } from "@nova/schemas"
import classNames from "classnames"
import { useCallback, useMemo, useState, useRef } from "react"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    selectHoveredRowId,
    selectOpenProjectDropdownFlightId,
    selectRenamingGroupId,
    selectTagDropdownFlightId,
} from "../redux/missions-table-selectors"
import {
    setHoveredRowId,
    setMissionSelected,
    setRenamingGroupId,
    type MissionsTableColumn,
} from "../redux/missions-table-slice"
import { useConfirmationModalContext } from "features/modals/WorkspaceConfirmationModalV2"
import { RenamableText } from "features/data-management/workspace/TODO-put-in-package/RenamableText"
import { selectActiveContextMenu } from "features/popups/contextMenu"
import { setActiveContextMenu } from "features/popups/slice"
import { getCompleteChecklistModalComponent } from "./CompleteChecklistModal"
import { useRenameGroup } from "./PilotTrackingMissionQueries"
import { useGroupDrop } from "./useMissionTableDnD"
import { MissionRow } from "./MissionRow"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { TriCheckbox } from "@nova/component-checkbox"

export const GroupRow = ({
    group,
    selectedIds,
    onMutate,
    columns,
    selectable = true,
    editable = true,
    origin,
    onPreviewChange,
    onHoverChange,
}: {
    group: MissionTableItem<"group">
    selectedIds: Record<string, boolean>
    onMutate: () => void
    columns: MissionsTableColumn[]
    selectable?: boolean
    editable?: boolean
    origin?: MissionOriginRouterState
    onPreviewChange?: (data: { anchorRect: DOMRect; missionId: string } | null) => void
    onHoverChange?: (missionId: string | null) => void
}) => {
    const hasChecklist = columns.includes("numChecklistSubmissions")
    const groupHeaderColSpan =
        (selectable ? 1 : 0) + columns.filter((c) => c !== "numChecklistSubmissions").length
    const totalCols = columns.length + (selectable ? 2 : 1)
    const [isOpen, setIsOpen] = useState(false)
    const dispatch = useAppDispatch()
    const modal = useConfirmationModalContext()
    const groupContextMenu = useAppSelector(selectActiveContextMenu("MissionGroup"))
    const renamingGroupId = useAppSelector(selectRenamingGroupId)
    const isRenaming = renamingGroupId === group.id
    const hoveredRowId = useAppSelector(selectHoveredRowId)
    const tagDropdownFlightId = useAppSelector(selectTagDropdownFlightId)
    const openProjectDropdownFlightId = useAppSelector(selectOpenProjectDropdownFlightId)

    const isDropdownOpenForThisGroup = groupContextMenu?.data.groupId === group.id
    const isDropdownOpenForAnotherFlight =
        !!tagDropdownFlightId ||
        !!openProjectDropdownFlightId ||
        (groupContextMenu && groupContextMenu.data.groupId !== group.id)
    const showActions =
        isDropdownOpenForThisGroup || (hoveredRowId === group.id && !isDropdownOpenForAnotherFlight)

    const { isEditor } = useWorkspacePermissions()
    const { renameGroup } = useRenameGroup(group.id)
    const { isOver: headerIsOver, dropRef: headerDropRef } = useGroupDrop(group.id, !isRenaming)

    const tags = Object.values(
        group.missions
            .flatMap((m) => m.tags)
            .reduce<Record<string, { id: string; name: string }>>((prev, curr) => {
                prev[curr.id] = curr
                return prev
            }, {})
    )

    const checkboxState = useMemo(() => {
        const checkedCount = group.missions.reduce((acc, cur) => {
            if (selectedIds[cur.id]) return acc + 1
            return acc
        }, 0)
        if (checkedCount === group.missions.length) return "checked"
        if (checkedCount > 0) return "indeterminate"
        return "unchecked"
    }, [group.missions, selectedIds])

    const toggleGroupSelectionState = useCallback(() => {
        group.missions.forEach((mission) =>
            dispatch(setMissionSelected({ id: mission.id, selected: checkboxState !== "checked" }))
        )
    }, [checkboxState, dispatch, group.missions])

    const missingCount = group.missions.filter((m) => m.numChecklistSubmissions === 0).length
    const total = group.missions.length

    const groupRowRef = useRef<HTMLTableRowElement>(null)
    headerDropRef(groupRowRef)

    return (
        <>
            <Table.Row
                ref={groupRowRef}
                data-drag-type="touch"
                onClick={isRenaming ? undefined : () => setIsOpen((prev) => !prev)}
                onMouseEnter={() => dispatch(setHoveredRowId(group.id))}
                onMouseLeave={() => dispatch(setHoveredRowId(null))}
                className={classNames("relative transition-colors cursor-pointer", {
                    "bg-gray-700": showActions,
                })}
            >
                {selectable && (
                    <Table.Cell className="w-10">
                        <div
                            className={classNames("transition-opacity", {
                                "opacity-0": !showActions && checkboxState === "unchecked",
                            })}
                        >
                            <TriCheckbox
                                state={checkboxState}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    toggleGroupSelectionState()
                                }}
                                size="small"
                            />
                        </div>
                    </Table.Cell>
                )}
                <Table.Cell className="w-8 !pl-2">
                    <Icons.SmallIcon
                        className={isOpen ? "text-white" : ""}
                        Icon={isOpen ? Icons.ArrowDropdown : Icons.ArrowDropdownRight}
                    />
                </Table.Cell>
                <Table.Cell
                    colSpan={(isRenaming ? totalCols : groupHeaderColSpan) - (hasChecklist ? 1 : 0)}
                    className="!pl-1.5"
                >
                    {headerIsOver && (
                        <div className="absolute bottom-0 left-10 right-0 h-0.5 bg-white/80 pointer-events-none" />
                    )}
                    <div className="min-h-[42px] flex items-center gap-4">
                        {isRenaming ? (
                            <div className="sticky left-0 w-[270px] pr-4">
                                <RenamableText
                                    Tag="h5"
                                    title={group.name}
                                    id={group.id}
                                    isRenaming
                                    saveOnBlur
                                    backgroundIsDark
                                    onConfirmRenaming={(_id, name) => {
                                        void renameGroup(name)
                                        dispatch(setRenamingGroupId(null))
                                    }}
                                    onCancelRenaming={() => dispatch(setRenamingGroupId(null))}
                                />
                            </div>
                        ) : (
                            <>
                                <span>{group.name}</span>
                                <div className="flex gap-1 items-center max-w-[275px] flex-wrap">
                                    {tags.map((tag) => (
                                        <Tag key={tag.id}>
                                            <small className="text-sm">{tag.name}</small>
                                        </Tag>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </Table.Cell>
                {!isRenaming && (
                    <>
                        {hasChecklist && (
                            <Table.Cell>
                                <Status
                                    type={missingCount > 0 ? "error" : "positive"}
                                    variant="outline"
                                >
                                    {missingCount > 0
                                        ? `${missingCount}/${total} Missing`
                                        : "Completed"}
                                </Status>
                            </Table.Cell>
                        )}
                        <Table.Cell className="sticky right-0 w-px">
                            <div
                                className={classNames(
                                    "absolute right-0 top-0 bottom-0 items-center gap-4 bg-gray-700 px-4 py-2 z-10 flex transition-opacity",
                                    {
                                        "pointer-events-none opacity-0": !showActions,
                                        "pointer-events-auto": showActions,
                                    }
                                )}
                            >
                                <ButtonPrimary
                                    title="Complete Checklist"
                                    RightIcon={Icons.Docs}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        void modal.confirm({
                                            RenderComponent: getCompleteChecklistModalComponent({
                                                missionIds: group.missions.map((m) => m.id),
                                            }),
                                        })
                                    }}
                                />
                                {isEditor && editable && (
                                    <IconButton
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            if (groupContextMenu?.data.groupId === group.id) {
                                                dispatch(setActiveContextMenu(undefined))
                                                return
                                            }
                                            const rect = (
                                                e.currentTarget as HTMLElement
                                            ).getBoundingClientRect()
                                            dispatch(
                                                setActiveContextMenu({
                                                    type: "MissionGroup",
                                                    data: { groupId: group.id },
                                                    position: { top: rect.bottom, left: rect.left },
                                                })
                                            )
                                        }}
                                    >
                                        <Icons.Icon Icon={Icons.ThreeDotsSettings} />
                                    </IconButton>
                                )}
                            </div>
                        </Table.Cell>
                    </>
                )}
            </Table.Row>
            {isOpen &&
                group.missions.map((m) => (
                    <MissionRow
                        key={`${group.id}-${m.id}`}
                        mission={m}
                        selectedIds={selectedIds}
                        onMutate={onMutate}
                        columns={columns}
                        selectable={selectable}
                        editable={editable}
                        nested
                        groupId={group.id}
                        origin={origin}
                        onPreviewChange={onPreviewChange}
                        onHoverChange={onHoverChange}
                    />
                ))}
        </>
    )
}
